/**
 * Right-side citation drawer. Opens when a user clicks a citation pill in chat.
 * Renders the matching chunk excerpts with:
 *   - document name + page label
 *   - retrieval score (0–100) + matched query keywords as pills
 *   - excerpt text with the matched keywords *and* the answer's cited phrase
 *     highlighted in-line so the reader can see exactly why this chunk won.
 *   - Open document link
 */
import { X, FileText, ExternalLink } from "lucide-react";

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Highlight any occurrence of `terms` (case-insensitive, word boundary aware
 * where possible) inside `text` and return an array of React nodes.
 * Longer terms are matched first so multi-word phrases beat single words.
 */
function highlightExcerpt(text, terms) {
  if (!text || !terms || terms.length === 0) return text;
  const clean = terms
    .map(t => (t || "").trim())
    .filter(t => t.length >= 2)
    .sort((a, b) => b.length - a.length);
  if (clean.length === 0) return text;

  const pattern = new RegExp(`(${clean.map(escapeRegExp).join("|")})`, "gi");
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return (
        <mark
          key={i}
          className="bg-[var(--aria-accent)]/22 text-[var(--aria-text)] rounded-[2px] px-0.5"
        >
          {part}
        </mark>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function CitationDrawer({ open, onClose, citations, focusedName, onOpenDocument }) {
  const filtered = focusedName
    ? (citations || []).filter((c) => c.name === focusedName)
    : (citations || []);
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[80] bg-black/45 backdrop-blur-[2px] transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="citation-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        data-testid="citation-drawer"
        className={`fixed top-0 right-0 z-[90] h-full w-full sm:w-[520px] bg-[var(--aria-surface)] border-l border-[var(--aria-border)] shadow-[-12px_0_60px_rgba(229,0,104,0.18)] transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <header className="flex items-start justify-between px-6 py-5 border-b border-[var(--aria-border)]">
          <div>
            <div className="text-[10px] font-mono tracking-[0.28em] text-[var(--aria-primary)] uppercase">
              ◆ Source · cite
            </div>
            <h3 className="font-display text-2xl tracking-tight mt-1 truncate max-w-[380px]" title={focusedName}>
              {focusedName || "All sources"}
            </h3>
            <p className="text-xs text-[var(--aria-text-muted)] mt-1">
              {filtered.length} matching {filtered.length === 1 ? "excerpt" : "excerpts"}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="citation-drawer-close"
            className="p-2 -mr-2 rounded-sm border border-transparent hover:border-[var(--aria-border)] hover:bg-[var(--aria-surface-2)] transition-colors"
          >
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto h-[calc(100%-90px)] px-6 py-5 space-y-4">
          {filtered.length === 0 && (
            <div className="text-sm text-[var(--aria-text-muted)] italic">
              No excerpts available.
            </div>
          )}
          {filtered.map((c, i) => {
            const terms = c.matched_keywords || [];
            const scorePct = Math.round(Math.max(0, Math.min(1, c.score_norm ?? 0)) * 100);
            return (
              <div
                key={`${c.chunk_id || i}-${i}`}
                data-testid={`citation-excerpt-${i}`}
                className="aria-card p-4 fade-in"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-xs text-[var(--aria-text-muted)] min-w-0">
                    <FileText size={13} className="text-[var(--aria-primary)] shrink-0" />
                    <span className="truncate font-medium" title={c.name}>{c.name}</span>
                    {(c.page !== undefined && c.page !== null) ? (
                      <span className="aria-pill text-[9px] shrink-0">p. {c.page}</span>
                    ) : (c.chunk_index !== undefined && c.chunk_index !== null) && (
                      <span className="aria-pill text-[9px] shrink-0">¶ {c.chunk_index + 1}</span>
                    )}
                  </div>
                  {typeof c.score_norm === "number" && (
                    <div
                      data-testid={`citation-score-${i}`}
                      title={`Retrieval score ${c.score?.toFixed?.(1) ?? c.score}`}
                      className="flex items-center gap-1.5 shrink-0"
                    >
                      <div className="h-[3px] w-14 bg-[var(--aria-surface-2)] rounded-sm overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${scorePct}%`,
                            background: "var(--aria-grad-brand)",
                          }}
                        />
                      </div>
                      <span className="text-[9px] font-mono text-[var(--aria-text-muted)] tabular-nums w-6 text-right">
                        {scorePct}
                      </span>
                    </div>
                  )}
                </div>
                {terms.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {terms.slice(0, 8).map((t) => (
                      <span
                        key={t}
                        className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 border border-[var(--aria-accent)]/40 text-[var(--aria-accent)] bg-[var(--aria-accent)]/6 rounded-sm"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-sm leading-relaxed text-[var(--aria-text)] whitespace-pre-wrap">
                  {highlightExcerpt(c.excerpt || "", terms)}
                  {c.excerpt && c.excerpt.length >= 600 && <span className="text-[var(--aria-text-faint)]"> …</span>}
                </p>
                {c.project_id && <p className="mt-2 text-xs text-[var(--aria-text-muted)]" data-testid={`citation-provenance-${i}`}>{c.project_name} · {c.source} · v{c.version}</p>}
                {c.doc_id && (
                  <button
                    onClick={() => onOpenDocument?.(c)}
                    data-testid={`citation-open-doc-${i}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--aria-primary)] hover:underline font-mono uppercase tracking-[0.18em]"
                  >
                    open in source <ExternalLink size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}
